"use client";
import { useEffect, useState } from "react";
import { DataTable } from "~/components/shadcn/DataTable";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import { EventAndCommunity } from "../actions";
import { columns } from "./columns";
import { If } from "~/components/v3/Conditional";
import { SponsorshipPackageTier } from "@prisma/client";

const CommunityTagsFilter = ({
    community,
    events,
    setFilteredEvents,
}: {
    community:
        | {
              defaultTags: PrismaJson.DefaultTags[];
          }
        | undefined
        | null;
    events: EventAndCommunity[];
    setFilteredEvents: (events: EventAndCommunity[]) => void;
}) => {
    // Select works with onKeyDown, which bugs the general scroll on mobile, this overrides the behavior
    const [open, setOpen] = useState<number>();
    const [tags, setTags] = useState<string[]>([]);

    useEffect(() => {
        if (!tags.length) {
            setFilteredEvents(events);
            return;
        }
        const filtered = events?.filter((e) =>
            e.tags?.some((t) => tags.includes(t)),
        );
        setFilteredEvents(filtered);
    }, [tags]);

    if (!community) return null;

    return (
        <div className="flex h-14 w-full items-center gap-4 md:max-w-5xl">
            <h5 className="text-lg font-[500] leading-none">Tags</h5>
            <span className="small-scrollbar flex h-full w-[calc(100vw-40px-32px)] items-center justify-start gap-2 overflow-x-auto px-1 pb-1">
                {community.defaultTags.map((customTags, index) => {
                    return (
                        <Select
                            key={index}
                            open={open === index}
                            onOpenChange={() => setOpen(undefined)}
                            onValueChange={(e) => {
                                // Find tags of this group that are already selected
                                const tagGroup = tags.filter((t) =>
                                    customTags.tags?.includes(t),
                                );

                                // filter the old tag from group
                                const newTags = tags.filter(
                                    (t) => !tagGroup.includes(t),
                                );

                                // Add new if it's not a group name
                                if (e !== customTags.name) {
                                    newTags.push(e);
                                }

                                setTags(newTags);
                            }}
                        >
                            <SelectTrigger
                                className="h-9 w-full min-w-[150px] max-w-[150px] rounded-full text-start"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(index);
                                }}
                            >
                                <SelectValue placeholder={customTags.name} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem value={customTags.name}>
                                        {customTags.name}
                                    </SelectItem>
                                    {/* I hate typescript lol */}
                                    {community.defaultTags &&
                                        customTags.tags?.map(
                                            (tagName: string) => (
                                                <SelectItem
                                                    key={tagName}
                                                    value={tagName}
                                                >
                                                    {tagName}
                                                </SelectItem>
                                            ),
                                        )}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    );
                })}
            </span>
        </div>
    );
};

interface Props {
    events: EventAndCommunity[];
    searchOptions?: {
        defaultTags: PrismaJson.DefaultTags[];
    } | null;
    showBorder?: boolean;
    showTagsFilter?: boolean;
    selectable?: boolean;
    selectedIds?: Set<string>;
    selectedTier?: SponsorshipPackageTier | null;
    onSelectionChange?: (selectedIds: Set<string>) => void;
}

const SearchableTable = ({
    events,
    searchOptions,
    showBorder = false,
    showTagsFilter = false,
    selectable = false,
    selectedTier,
    selectedIds = new Set(),
    onSelectionChange,
}: Props) => {
    const [filteredEvents, setFilteredEvents] = useState(events);

    const handleSelectionChange = (eventId: string) => {
        const newSelection = new Set(selectedIds);
        if (newSelection.has(eventId)) {
            newSelection.delete(eventId);
        } else {
            newSelection.add(eventId);
        }
        onSelectionChange?.(newSelection);
    };

    if (!filteredEvents) return null;

    return (
        <div className="mb-20 flex h-full w-full flex-col items-center gap-5 overflow-x-auto overflow-y-hidden">
            <If condition={showTagsFilter}>
                <div className="flex w-full flex-col gap-2">
                    <CommunityTagsFilter
                        community={searchOptions}
                        events={events}
                        setFilteredEvents={setFilteredEvents}
                    />
                </div>
            </If>
            <DataTable
                columns={columns}
                data={filteredEvents}
                className="h-max max-h-none"
                showBorders={showBorder}
                meta={
                    selectable
                        ? {
                              selectedIds,
                              selectedTier,
                              onSelectionChange: handleSelectionChange,
                          }
                        : undefined
                }
            />
        </div>
    );
};

export default SearchableTable;
